import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Wallet, UsersRound, ShieldCheck, Users, ArrowRight, TrendingUp, Loader2, Inbox } from "lucide-react";

function StatTile({ icon: Icon, label, value, hint, href, testid }) {
  const content = (
    <div className="bg-white border border-sg-line rounded-2xl p-6 hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
          <Icon className="w-4 h-4" />
        </div>
        <div className="overline text-sg-gold-deep text-[11px]">{label}</div>
      </div>
      <div className="mt-4 font-display text-4xl text-sg-navy">{value}</div>
      {hint && <div className="mt-1 text-xs text-sg-mute">{hint}</div>}
      {href && <div className="mt-4 text-sm text-sg-gold-deep inline-flex items-center gap-1">View <ArrowRight className="w-3 h-3" /></div>}
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ agents: 0, active: 0, mrr: 0, pending: 0, users: 0, leads: 0, contact_submissions: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [agents, ledger, users, leads, contact] = await Promise.all([
          api.get("/admin/agents"),
          api.get("/admin/ledger"),
          api.get("/admin/users"),
          api.get("/admin/leads"),
          api.get("/admin/contact-submissions").catch(() => ({ data: { total: 0 } })),
        ]);
        if (cancelled) return;
        const allAgents = agents.data.agents || [];
        setData({
          agents: allAgents.length,
          active: ledger.data.active_count || 0,
          mrr: ledger.data.monthly_revenue_inr || 0,
          pending: allAgents.filter((a) => a.subscription_status === "pending").length,
          users: (users.data.users || []).length,
          leads: leads.data.total || 0,
          contact_submissions: contact.data.total || 0,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardShell>
      <div data-testid="admin-overview">
        <div className="overline text-sg-gold-deep">PLATFORM OWNER · CONTROL ROOM</div>
        <h1 className="font-display text-4xl text-sg-navy mt-1">Welcome, <span className="font-serif-italic text-sg-gold-deep">{(user?.name || "Owner").split(" ")[0]}</span>.</h1>
        <p className="text-sg-mute mt-2 max-w-2xl">Live platform health — agents, paid subscriptions, leads.</p>

        {loading ? (
          <div className="mt-10 text-sg-mute inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatTile icon={UsersRound} label="REGISTERED AGENTS" value={data.agents} hint={`${data.pending} pending payment`} href="/admin/agents" testid="stat-agents" />
            <StatTile icon={Wallet} label="ACTIVE SUBSCRIPTIONS" value={data.active} hint={`MRR · ₹${data.mrr.toLocaleString("en-IN")}`} href="/admin/ledger" testid="stat-active" />
            <StatTile icon={TrendingUp} label="MONTHLY REVENUE" value={`₹${data.mrr.toLocaleString("en-IN")}`} hint="from active agents" href="/admin/ledger" testid="stat-mrr" />
            <StatTile icon={ShieldCheck} label="OWNER ACCOUNTS" value={data.users} href="/admin/users" testid="stat-users" />
            <StatTile icon={Users} label="TOTAL LEADS (LIFETIME)" value={data.leads} href="/admin/leads" testid="stat-leads" />
            <StatTile icon={Inbox} label="CONTACT ENQUIRIES" value={data.contact_submissions} hint="from public Contact Us form" href="/admin/contact" testid="stat-contact" />
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
